import api from './api';

export interface PaymentMethod {
  id: string;
  name: string;
  icon?: string;
  supported: boolean;
}

export interface CustomerInfo {
  id?: number;
  name: string;
  phone: string;
  email?: string;
  country_code?: string;
}

export interface PaymentLinkRequest {
  invoice_id: number;
  amount: number;
  currency?: string;
  description?: string;
  customer: CustomerInfo;
}

export interface CardPaymentRequest {
  amount: number;
  currency?: string;
  invoice_id?: number;
  customer_id: number;
  source_id: string; // Token from TAP Card SDK
  description?: string;
}

export interface PaymentResult {
  payment_url?: string;
  payment_id: string;
  reference?: string;
  success: boolean;
  message: string;
  data?: any;
}

export interface PaymentStatus {
  payment_id: string;
  status: string;
  amount: number;
  currency: string;
  payment_method: string;
  updated_at: string;
}

export const paymentAPI = {
  // TAP Payments Integration
  
  // Create payment link for invoice
  createPaymentLink: (data: PaymentLinkRequest) =>
    api.post<PaymentResult>('/payments/tap/payment-link', data),
  
  // Process direct card payment
  processCardPayment: (data: CardPaymentRequest) =>
    api.post<PaymentResult>('/payments/tap/card-payment', data),
  
  // Verify payment status
  verifyPayment: (paymentId: string) =>
    api.get<PaymentStatus>(`/payments/tap/verify/${paymentId}`),
  
  // Get supported payment methods
  getPaymentMethods: () =>
    api.get<PaymentMethod[]>('/payments/methods'),
  
  // General Payment Operations
  
  // Get all payments
  getPayments: (params?: {
    status?: string;
    payment_method?: string;
    customer_id?: number;
    invoice_id?: number;
    start_date?: string;
    end_date?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => api.get('/payments', { params }),
  
  // Get single payment
  getPayment: (id: number) =>
    api.get(`/payments/${id}`),
  
  // Create payment record
  createPayment: (data: any) =>
    api.post('/payments', data),
  
  // Update payment
  updatePayment: (id: number, data: any) =>
    api.put(`/payments/${id}`, data),
  
  // Delete payment
  deletePayment: (id: number) =>
    api.delete(`/payments/${id}`),
  
  // Payment actions
  refundPayment: (id: number, data: { amount: number; reason: string }) =>
    api.post(`/payments/${id}/refund`, data),
  
  allocatePayment: (id: number, data: { allocations: Array<{ invoice_id: number; amount: number }> }) =>
    api.post(`/payments/${id}/allocate`, data),
  
  // Customer and invoice payments
  getCustomerPayments: (customerId: number) =>
    api.get(`/payments/customer/${customerId}`),
  
  getInvoicePayments: (invoiceId: number) =>
    api.get(`/payments/invoice/${invoiceId}`),
  
  // Statistics
  getPaymentStats: (period?: string) =>
    api.get('/payments/stats', { params: { period } }),
};

// TAP Payment Utils

export const TAP_CONFIG = {
  // TAP SDK Configuration (these would come from env variables in production)
  publicKey: 'pk_test_your_tap_public_key', // Replace with actual key
  country: 'KW', // Kuwait
  currency: 'KWD',
  supportedPaymentMethods: ['knet', 'card', 'apple_pay', 'google_pay', 'samsung_pay'],
};

// Payment status helpers
export const PaymentStatus = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
  REFUNDED: 'refunded',
  CANCELLED: 'cancelled',
} as const;

export const getPaymentStatusColor = (status: string) => {
  switch (status) {
    case PaymentStatus.COMPLETED:
      return 'bg-green-100 text-green-800';
    case PaymentStatus.PENDING:
      return 'bg-yellow-100 text-yellow-800';
    case PaymentStatus.FAILED:
      return 'bg-red-100 text-red-800';
    case PaymentStatus.REFUNDED:
      return 'bg-purple-100 text-purple-800';
    case PaymentStatus.CANCELLED:
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const formatCurrency = (amount: number, currency: string = 'KWD') => {
  return new Intl.NumberFormat('ar-KW', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(amount);
};

// TAP SDK Integration helpers
declare global {
  interface Window {
    Tap: any;
  }
}

export const loadTapSDK = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    if (window.Tap) {
      resolve(window.Tap);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://secure.gosell.io/js/sdk/tap.min.js';
    script.onload = () => {
      if (window.Tap) {
        resolve(window.Tap);
      } else {
        reject(new Error('TAP SDK failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load TAP SDK'));
    document.head.appendChild(script);
  });
};

export const initializeTapPayment = async (containerId: string, config: {
  amount: number;
  currency: string;
  customer: CustomerInfo;
  onSuccess: (response: any) => void;
  onError: (error: any) => void;
}) => {
  try {
    const Tap = await loadTapSDK();
    
    const tapConfig = {
      publicKey: TAP_CONFIG.publicKey,
      merchant: {
        id: '', // Your merchant ID
      },
      transaction: {
        amount: config.amount,
        currency: config.currency,
      },
      customer: {
        first_name: config.customer.name.split(' ')[0],
        last_name: config.customer.name.split(' ').slice(1).join(' '),
        phone: {
          country_code: config.customer.country_code || '+965',
          number: config.customer.phone,
        },
        email: config.customer.email,
      },
      interface: {
        locale: 'en',
        theme: 'light',
        edges: 'curved',
        direction: 'ltr',
      },
      acceptance: {
        supportedSchemes: ['knet', 'visa', 'mastercard', 'amex'],
        supportedFundSource: ['credit', 'debit'],
        supportedPaymentMethods: ['knet', 'card', 'apple_pay', 'google_pay'],
      },
      fields: {
        card: {
          cardNumber: true,
          expirationDate: true,
          cvv: true,
          cardHolder: true,
        },
      },
      addons: {
        displayPaymentBrands: true,
        loader: true,
        saveCard: false,
      },
      onReady: () => {
        console.log('TAP Payment form is ready');
      },
      onFocus: (data: any) => {
        console.log('Field focused:', data);
      },
      onValidInput: (data: any) => {
        console.log('Valid input:', data);
      },
      onInvalidInput: (data: any) => {
        console.log('Invalid input:', data);
      },
      onError: (error: any) => {
        console.error('TAP Payment error:', error);
        config.onError(error);
      },
      onSuccess: (response: any) => {
        console.log('TAP Payment success:', response);
        config.onSuccess(response);
      },
    };

    // Create TAP payment form
    return Tap.showPaymentForm(containerId, tapConfig);
  } catch (error) {
    console.error('Error initializing TAP payment:', error);
    config.onError(error);
  }
};